import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import vendorApi from '../../services/vendorApi';
import { Calendar, Home, Bed, Star, Loader2 } from 'lucide-react';
import { AdminPageHeader, adminCardClass, ADMIN_ICON_STROKE } from '../../components/admin/AdminShared';

const PERIOD_MAP = {
  'This Week': 'week',
  'This Month': 'month',
  'This Year': 'year'
};

const VendorGrowth = () => {
  const { user, loading, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [timeFilter, setTimeFilter] = useState('This Week');
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState('');

  useEffect(() => {
    if (!loading) {
      if (!isAuthenticated || !user) {
        navigate('/vendor/login', { replace: true });
        return;
      }
      if (user.role !== 'vendor' && user.role !== 'admin') {
        navigate('/vendor/login', { replace: true, state: { needsVendorAccount: true } });
        return;
      }
    }
  }, [user, loading, isAuthenticated, navigate]);

  useEffect(() => {
    let cancelled = false;
    const period = PERIOD_MAP[timeFilter] || 'week';
    const load = async () => {
      if (!user || (user.role !== 'vendor' && user.role !== 'admin')) return;
      try {
        setStatsLoading(true);
        setStatsError('');
        const data = await vendorApi.getGrowthStats(period);
        if (!cancelled) setStats(data);
      } catch (e) {
        if (!cancelled) {
          setStatsError(e.response?.data?.error || 'Could not load stats');
          setStats(null);
        }
      } finally {
        if (!cancelled) setStatsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [user, timeFilter]);

  const greeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const displayName =
    user?.name ||
    [user?.firstName, user?.lastName].filter(Boolean).join(' ') ||
    'there';

  if (loading || !isAuthenticated || !user || (user.role !== 'vendor' && user.role !== 'admin')) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAF7F2]">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-[#FF5E4C] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminPageHeader title="Growth" subtitle={`${greeting()}, ${displayName} — earnings and activity at a glance.`} />

      <div className="space-y-4">
        {statsError && (
          <div className="p-3 rounded-[12px] bg-red-50 text-red-800 text-sm border border-red-100">{statsError}</div>
        )}

        <div className={`${adminCardClass} p-5 md:p-6`}>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
            <h2 className="text-[16px] font-medium text-black">Total earnings</h2>
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className="text-[13px] border border-[#E5E3DF] rounded-[12px] px-3 py-2 bg-white text-black focus:outline-none focus:ring-2 focus:ring-[#FF5E4C]/30 focus:border-[#FF5E4C]"
            >
              <option>This Week</option>
              <option>This Month</option>
              <option>This Year</option>
            </select>
          </div>
          {statsLoading ? (
            <div className="flex items-center gap-2 py-4">
              <Loader2 className="w-8 h-8 text-[#FF5E4C] animate-spin" strokeWidth={ADMIN_ICON_STROKE} />
              <span className="text-[#78736A]">Loading…</span>
            </div>
          ) : (
            <p className="text-3xl md:text-4xl font-medium text-black tabular-nums">
              {stats?.totalEarningsFormatted ?? '₹0'}
            </p>
          )}
          <p className="text-[12px] text-[#78736A] mt-3 leading-relaxed">
            Confirmed booking totals by payment date in the selected period (paid / confirmed only).
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className={`${adminCardClass} p-5`}>
            <Calendar className="w-7 h-7 text-[#FF5E4C] mb-3" strokeWidth={ADMIN_ICON_STROKE} />
            <p className="text-[13px] text-[#78736A] mb-1">Upcoming bookings</p>
            {statsLoading ? (
              <Loader2 className="w-6 h-6 text-[#E5E3DF] animate-spin" />
            ) : (
              <p className="text-2xl font-medium text-black tabular-nums">{stats?.upcomingBookings ?? 0}</p>
            )}
          </div>

          <div className={`${adminCardClass} p-5`}>
            <Home className="w-7 h-7 text-[#FF5E4C] mb-3" strokeWidth={ADMIN_ICON_STROKE} />
            <p className="text-[13px] text-[#78736A] mb-1">In-house bookings</p>
            {statsLoading ? (
              <Loader2 className="w-6 h-6 text-[#E5E3DF] animate-spin" />
            ) : (
              <p className="text-2xl font-medium text-black tabular-nums">{stats?.inHouseBookings ?? 0}</p>
            )}
          </div>

          <div className={`${adminCardClass} p-5`}>
            <Bed className="w-7 h-7 text-[#FF5E4C] mb-3" strokeWidth={ADMIN_ICON_STROKE} />
            <p className="text-[13px] text-[#78736A] mb-1">Total rooms</p>
            {statsLoading ? (
              <Loader2 className="w-6 h-6 text-[#E5E3DF] animate-spin" />
            ) : (
              <p className="text-2xl font-medium text-black tabular-nums">{stats?.totalRooms ?? 0}</p>
            )}
            <p className="text-[12px] text-[#78736A] mt-2">Sum of capacities</p>
          </div>

          <div className={`${adminCardClass} p-5`}>
            <Star className="w-7 h-7 text-[#FF5E4C] mb-3" strokeWidth={ADMIN_ICON_STROKE} />
            <p className="text-[13px] text-[#78736A] mb-1">Reviews</p>
            {statsLoading ? (
              <Loader2 className="w-6 h-6 text-[#E5E3DF] animate-spin" />
            ) : (
              <p className="text-2xl font-medium text-black tabular-nums">{stats?.latestReviews ?? 0}</p>
            )}
            <p className="text-[12px] text-[#78736A] mt-2">On your listings</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VendorGrowth;
